import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Tooltip } from '@mui/material';
import LabelChip from './LabelChip';

const LabelChipContainer = ({ labels }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [visibleLabels, setVisibleLabels] = useState([]);
  const [showEllipsis, setShowEllipsis] = useState(false);

  // ResizeObserver to detect container width changes
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width } = entry.contentRect;
        setContainerWidth(width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate visible chips based on actual measurements
  const calculateVisibleChips = useCallback(() => {
    if (!containerRef.current || !labels || labels.length === 0 || containerWidth === 0) {
      setVisibleLabels([]);
      setShowEllipsis(false);
      return;
    }

    // Create a temporary container for measurement
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.top = '-9999px';
    tempContainer.style.display = 'flex';
    tempContainer.style.gap = '4px';
    tempContainer.style.alignItems = 'center';
    document.body.appendChild(tempContainer);

    try {
      const ellipsisWidth = 20; // Width for "..." text
      const padding = 16; // Container padding
      const availableWidth = containerWidth - padding;
      
      let currentWidth = 0;
      const fitting = [];

      for (let i = 0; i < labels.length; i++) {
        // Create temporary chip element
        const tempChip = document.createElement('div');
        tempChip.className = 'MuiChip-root MuiChip-filled MuiChip-sizeSmall';
        tempChip.style.cssText = `
          margin: 2px;
          padding: 4px 8px;
          background-color: #E8E8E8;
          border-radius: 12px;
          font-size: 8px;
          height: 24px;
          display: inline-flex;
          align-items: center;
          white-space: nowrap;
        `;
        
        const labelText = labels[i].name || labels[i];
        tempChip.textContent = labelText;
        tempContainer.appendChild(tempChip);
        
        const chipWidth = tempChip.offsetWidth;
        const gapWidth = i > 0 ? 4 : 0; // Gap between chips
        
        // Check if we need to reserve space for ellipsis
        const willNeedEllipsis = i < labels.length - 1;
        const requiredWidth = currentWidth + gapWidth + chipWidth + (willNeedEllipsis ? ellipsisWidth + 4 : 0);
        
        if (requiredWidth > availableWidth) {
          // This chip doesn't fit
          setShowEllipsis(fitting.length < labels.length);
          break;
        }
        
        fitting.push(labels[i]);
        currentWidth += gapWidth + chipWidth;
        
        // If this was the last chip, no ellipsis needed
        if (i === labels.length - 1) {
          setShowEllipsis(false);
        }
      }
      
      setVisibleLabels(fitting);
      
    } finally {
      document.body.removeChild(tempContainer);
    }
  }, [labels, containerWidth]);

  useEffect(() => {
    calculateVisibleChips();
  }, [calculateVisibleChips]);

  // Get hidden labels for tooltip
  const hiddenLabels = labels ? labels.slice(visibleLabels.length) : [];
  const hiddenLabelsText = hiddenLabels.map(label => label.name || label).join(', ');

  if (!labels || labels.length === 0) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: '4px',
        height: 'auto',
        maxHeight: 60, // allow at least 2 rows (2 * 24px chip height + gap)
        overflow: 'hidden',
        whiteSpace: 'normal',
        width: '100%',
        padding: '0 8px',
      }}
    >
      {visibleLabels.map((label, index) => (
        <LabelChip
          key={label.id || label.name || index}
          label={label}
          sx={{
            flexShrink: 0,
            minWidth: 60,
            maxWidth: 120,
            mb: 0.5,
          }}
        />
      ))}
      {showEllipsis && (
        <Tooltip 
          title={`Hidden: ${hiddenLabelsText}`} 
          placement="top" 
          arrow
        >
          <Box
            sx={{
              color: '#666',
              fontSize: '10px',
              fontWeight: 'bold',
              cursor: 'help',
              padding: '0 4px',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            ...
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default LabelChipContainer;